import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import axios from "axios";
import { toast } from "react-toastify";
import Select from "../../components/select";

import { useLanguage } from "../../context/languageContext";
import Button from "../../components/button";
import { useAlreadyExists } from "../../components/confirmDialog";

const API = process.env.REACT_APP_API_ROOT;

/**
 * Create or edit a tag. `tagId` null => create. A tag is a single keyword plus
 * zero or more genres (the genre picker is optional).
 */
const AddEditPopup = (props) => {
    const { adminInfo } = useLanguage();
    const showAlreadyExists = useAlreadyExists();
    const { isVisible, tagId, handleCloseModal, onSuccess } = props;

    const isCreate = !tagId;

    const [name, setName] = useState("");
    const [selectedGenres, setSelectedGenres] = useState([]);
    // The genreId of the primary genre. Must be one of the selected genres;
    // the rest are secondary. Null only when no genres are attached.
    const [primaryGenreId, setPrimaryGenreId] = useState(null);
    const [genreOptions, setGenreOptions] = useState([]);
    const [errors, setErrors] = useState({});
    const [isSubmit, setIsSubmit] = useState(false);

    // Load the genre list for the picker (English label, genre id as value).
    useEffect(() => {
        if (!isVisible || !adminInfo?.token) return;
        (async () => {
            try {
                const headers = { Authorization: `Bearer ${adminInfo.token}` };
                const { data: response } = await axios.get(`${API}/api/admin/genre/getall`, { headers });
                const all = response?.data?.allGenres || [];
                setGenreOptions(
                    all
                        .map((g) => ({
                            value: g._id,
                            label: g.translations?.find((t) => t.langCode === "en")?.name || "(unnamed)",
                        }))
                        .filter((o) => o.value)
                );
            } catch (error) {
                console.error("fetchGenres catch error::", error);
            }
        })();
    }, [isVisible, adminInfo]);

    // Load the tag being edited.
    useEffect(() => {
        if (!isVisible) return;
        setErrors({});
        if (tagId) {
            (async () => {
                try {
                    const headers = { Authorization: `Bearer ${adminInfo.token}` };
                    const { data: response } = await axios.get(`${API}/api/admin/tag/get/${tagId}`, { headers });
                    if (response.status) {
                        setName(response.data?.name || "");
                        const genres = response.data?.genres || [];
                        setSelectedGenres(genres.map((g) => ({ value: g.genreId, label: g.genreName })));
                        const primary = genres.find((g) => g.isPrimary) || genres[0];
                        setPrimaryGenreId(primary ? primary.genreId : null);
                    } else {
                        toast.error(response.message || "Could not load this tag.");
                    }
                } catch (error) {
                    console.error("fetchTag catch error::", error);
                    toast.error("Could not load this tag.");
                }
            })();
        } else {
            setName("");
            setSelectedGenres([]);
            setPrimaryGenreId(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible, tagId]);

    // Keep the primary in sync with the selection: default to the first genre
    // when unset, and clear/reset it if the currently-primary genre is removed.
    useEffect(() => {
        if (selectedGenres.length === 0) {
            if (primaryGenreId !== null) setPrimaryGenreId(null);
            return;
        }
        const stillSelected = selectedGenres.some((g) => g.value === primaryGenreId);
        if (!stillSelected) setPrimaryGenreId(selectedGenres[0].value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedGenres]);

    const handleSubmit = async () => {
        if (!name.trim()) {
            setErrors({ name: "Please enter a tag keyword." });
            return;
        }

        try {
            setIsSubmit(true);
            const headers = { Authorization: `Bearer ${adminInfo.token}` };
            const payload = {
                name: name.trim(),
                genres: selectedGenres.map((g) => ({
                    genreId: g.value,
                    genreName: g.label,
                    isPrimary: g.value === primaryGenreId,
                })),
            };

            const url = isCreate
                ? `${API}/api/admin/tag/add`
                : `${API}/api/admin/tag/update/${tagId}`;
            const { data: response } = await axios[isCreate ? "post" : "put"](url, payload, { headers });

            if (response.status) {
                onSuccess();
                handleClose();
                toast.success(isCreate ? "Tag created." : "Tag updated.");
            } else {
                toast.error(response.message || "Could not save this tag.");
            }
        } catch (error) {
            console.error("handleSubmit catch error::", error);
            if (!showAlreadyExists(error)) toast.error(error.response?.data?.error || "Could not save this tag.");
        } finally {
            setIsSubmit(false);
        }
    };

    const handleClose = () => {
        handleCloseModal();
        setName("");
        setSelectedGenres([]);
        setPrimaryGenreId(null);
        setErrors({});
    };

    const primaryOption =
        selectedGenres.find((g) => g.value === primaryGenreId) || null;

    return (
        <Modal isOpen={isVisible} onRequestClose={handleClose} className="modal_body_sm" overlayClassName="modal_overlay">
            <div className="box_wrapper">
                <h4 className="fw-bold mb-3">{isCreate ? "Add Tag" : "Edit Tag"}</h4>

                <div className="modal_content_sm">
                    <div className="mb-3">
                        <label className="label">Keyword:</label>
                        <input
                            className="form_control"
                            type="text"
                            placeholder="e.g. Mindfulness"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                        {errors.name && <p className="error-message d-block">{errors.name}</p>}
                    </div>

                    <div className="mb-3">
                        <label className="label">Genres (optional):</label>
                        <Select
                            className="form-control-md fw-medium p-0"
                            isMulti
                            closeMenuOnSelect={false}
                            placeholder="Attach genres..."
                            value={selectedGenres}
                            onChange={(selected) => setSelectedGenres(selected || [])}
                            options={genreOptions}
                        />
                        <small className="text-muted">
                            A tag can belong to several genres, or none.
                        </small>
                    </div>

                    {selectedGenres.length > 0 && (
                        <div className="mb-3">
                            <label className="label">Primary genre:</label>
                            <Select
                                className="form-control-md fw-medium p-0"
                                placeholder="Choose the primary genre..."
                                value={primaryOption}
                                onChange={(selected) => setPrimaryGenreId(selected ? selected.value : null)}
                                options={selectedGenres}
                                isClearable={false}
                            />
                            <small className="text-muted">
                                One genre is primary (canonical); the rest are secondary.
                            </small>
                        </div>
                    )}
                </div>

                <div className="d-flex gap-2 justify-content-end mt-3">
                    <button type="button" className="btn btn-outline-secondary" onClick={handleClose} disabled={isSubmit}>Cancel</button>
                    <Button onClick={handleSubmit} disabled={isSubmit} isSubmit={isSubmit} label="Saving ..." text={isCreate ? "Create" : "Update"} />
                </div>
            </div>
        </Modal>
    );
};

export default AddEditPopup;
